import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import CurrencySwitcher from './CurrencySwitcher';
import { useLanguage } from '../hooks/useLanguage';
import { getMe as getMeApi, logout as logoutApi } from '../services/authApi';
import { useCurrency } from '../context/CurrencyContext';
import {
  PAINT_CATEGORIES,
  WORKWEAR_CATEGORIES,
  FEATURED_PAINT,
  FEATURED_WORKWEAR,
} from '../data/mockNavData';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [user, setUser] = useState(null);

  // Static mock data - no loading required
  const paintCats = PAINT_CATEGORIES;
  const workwearCats = WORKWEAR_CATEGORIES;
  const featuredPaint = FEATURED_PAINT;
  const featuredWorkwear = FEATURED_WORKWEAR;

  const { getLocalizedPath, currentLanguage } = useLanguage();
  const { formatPrice } = useCurrency();

  const isHomePage =
    location.pathname === `/${currentLanguage}` ||
    location.pathname === '/' ||
    location.pathname === `/${currentLanguage}/` ||
    location.pathname === `/${currentLanguage}/about-us` ||
    location.pathname === '/about-us' ||
    location.pathname === `/${currentLanguage}/about-us/` ||
    location.pathname === '/about-us/';

  // Debug logging
  console.log('Header State:', { isMenuOpen, isScrolled, isHomePage });

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  useEffect(() => {
    (async () => {
      try {
        const me = await getMeApi();
        if (me) {
          setUser(me);
          setIsLoggedIn(true);
        }
      } catch (e) {
        setIsLoggedIn(false);
        setUser(null);
      }
    })();
  }, []);

  const navigation = [
    { name: t('nav.home'), href: getLocalizedPath('/'), key: 'home' },
    {
      name: t('nav.workwear'),
      href: getLocalizedPath('/workwear'),
      key: 'workwear',
    },
    { name: t('nav.paint'), href: getLocalizedPath('/paint'), key: 'paint' },
    { name: t('nav.about'), href: getLocalizedPath('/about-us'), key: 'about' },
    {
      name: t('nav.collection'),
      href: getLocalizedPath('/collection'),
      key: 'collection',
    },

    {
      name: t('nav.orders'),
      href: getLocalizedPath('/orders'),
      key: 'orders',
    },
  ];

  const slugify = (s) =>
    s
      .toLowerCase()
      .replace(/\s*\(.*?\)\s*/g, '')
      .replace(/\s*\/\s*/g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .trim();

  const paintCategories = paintCats;
  const workwearCategories = workwearCats;

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Split an array into N nearly equal chunks (for mega menu columns)
  const splitIntoColumns = (arr, columns) => {
    const perCol = Math.ceil(arr.length / columns);
    return Array.from({ length: columns }, (_, i) =>
      arr.slice(i * perCol, i * perCol + perCol)
    );
  };

  const handleDropdownToggle = (type, isOpen) => {
    setOpenDropdown(isOpen ? type : null);
  };

  const handleMenuToggle = () => {
    if (isMenuOpen) {
      handleMenuClose();
    } else {
      setIsMenuOpen(true);
    }
  };

  const handleMenuClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const handleLogin = () => {
    navigate(getLocalizedPath('/login'));
  };

  const handleLogout = () => {
    logoutApi().finally(() => {
      setIsLoggedIn(false);
      setUser(null);
      setShowProfileDropdown(false);
    });
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown((v) => !v);
  };

  // Search State
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = React.useRef(null);

  // Debounced search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true);
        try {
          const params = new URLSearchParams();
          params.append('search', searchQuery);
          params.append('limit', '5');
          const data = await fetchProductsApi(params);
          setSearchResults(data.data || []);
        } catch (error) {
          console.error("Search fetch error:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Focus input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      // Small delay to allow transition
      setTimeout(() => searchInputRef.current.focus(), 100);
    }
  }, [isSearchOpen]);

  // Close search on clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSearchOpen && !event.target.closest('.search-container')) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  const handleSearchSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (searchQuery.trim()) {
      const path = getLocalizedPath(`/search?q=${encodeURIComponent(searchQuery)}`);
      console.log("Submitting search, navigating to (Hard):", path);
      window.location.href = path;
      // navigate(path); // Commented out to force hard reload
      // setIsSearchOpen(false); // No need to close if reloading
    }
  };

  return (
    <>
      {/* Search Drawer (Right Side) */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${isSearchOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        onClick={() => setIsSearchOpen(false)}
      />

      {/* Drawer Panel */}
      <div
        className={`search-container fixed top-0 right-0 h-full w-full sm:w-[450px] bg-white z-[70] shadow-2xl transform transition-transform duration-300 ease-in-out ${isSearchOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="h-full flex flex-col">
          {/* Drawer Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900">{t('search.title', "Search")}</h2>
            <button
              onClick={() => setIsSearchOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Search Input Area */}
          <div className="p-6 pb-2">
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t('search.placeholder', "What are you looking for?")}
                className="w-full bg-gray-50 text-base text-gray-900 placeholder-gray-500 border border-gray-200 rounded-lg outline-none px-4 py-3 pr-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {isSearching ? (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
              ) : (
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              )}
            </form>
          </div>

          {/* Results Area */}
          <div className="flex-1 overflow-y-auto p-6 pt-2">
            {(searchResults.length > 0) ? (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">{t('search.suggestions', "Suggestions")}</h3>
                {searchResults.map(product => (
                  <div
                    key={product._id || product.id}
                    onClick={() => {
                      const pid = product._id || product.id;
                      const path = getLocalizedPath(`/product/${pid}`);
                      console.log("Navigating to (Hard):", path);
                      window.location.href = path;
                      // navigate(path);
                      // setIsSearchOpen(false);
                      // window.scrollTo(0, 0);
                    }}
                    className="flex items-start gap-4 p-2 -mx-2 hover:bg-gray-50 rounded-lg transition-colors group cursor-pointer"
                  >
                    <div className="w-16 h-16 bg-gray-100 rounded-md overflow-hidden flex-shrink-0 border border-gray-100">
                      <img src={product.images?.[0] || '/assets/product-image.webp'} alt={product.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">{product.name}</h4>
                      <p className="text-sm text-gray-500 mt-1">{formatPrice(product.price)}</p>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSearchSubmit(e);
                  }}
                  className="w-full mt-4 py-3 bg-black text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  {t('search.viewAllResults', "View All Results")} ({searchResults.length}+)
                </button>
              </div>
            ) : searchQuery.length >= 2 && !isSearching ? (
              <div className="text-center py-10">
                <p className="text-gray-500">{t('search.noResults', "No results found")}</p>
              </div>
            ) : (
              // Empty State
              <div className="text-center py-10 opacity-60">
              </div>
            )}
          </div>
        </div>
      </div>

      <style>
        {`
                    @keyframes slideInFromRight {
                        from {
                            transform: translateX(100%);
                        }
                        to {
                            transform: translateX(0%);
                        }
                    }
                    
                    @keyframes slideOutToRight {
                        from {
                            transform: translateX(0%);
                        }
                        to {
                            transform: translateX(100%);
                        }
                    }
                    
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }
                `}
      </style>

      <nav
        className={`px-5 sm:px-6 lg:px-4 fixed top-0 z-50 w-full transition-all duration-300 ${isMenuOpen
          ? 'bg-black/50 backdrop-blur-lg'
          : isScrolled
            ? 'bg-black/50 backdrop-blur-lg'
            : 'bg-transparent'
          }`}
        style={{
          backgroundColor:
            isMenuOpen || isScrolled ? 'rgba(0, 0, 0, 0.50)' : 'transparent',
          backdropFilter: isMenuOpen || isScrolled ? 'blur(10px)' : 'none',
        }}
      >
        <div className="max-w-[1300px] mx-auto w-full h-[78px]">
          <div className="relative flex justify-between items-center h-full">
            <div className="hidden xl:flex items-center">
              {navigation.map((item) =>
                item.key === 'paint' ? (
                  <div
                    key={item.key}
                    className="px-2 first:pl-0 last:pr-0 relative"
                    onMouseEnter={() => setOpenDropdown('paint')}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <Link
                      to={item.href}
                      className={`group inline-flex items-center gap-1 text-[14px] leading-[16px] font-medium transition duration-200 border-b pb-[1.5px] ${isMenuOpen || isScrolled
                        ? 'text-white'
                        : isHomePage
                          ? 'text-white'
                          : 'text-black'
                        } ${isActive(item.href)
                          ? isMenuOpen || isScrolled
                            ? 'border-white'
                            : isHomePage
                              ? 'border-white'
                              : 'border-black'
                          : `border-transparent ${isMenuOpen || isScrolled
                            ? 'hover:border-white'
                            : isHomePage
                              ? 'hover:border-white'
                              : 'hover:border-black'
                          }`
                        }`}
                    >
                      {item.name}
                      <svg
                        className={`h-4 w-4 transition-transform duration-200 ${openDropdown === 'paint' ? 'rotate-180' : 'rotate-0'}`}
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.08496 6.73828L8.08496 10.7383L12.085 6.73828"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>
                    {/* paint */}
                    {openDropdown === 'paint' && !isMenuOpen && (
                      <div className="absolute left-0 top-full pt-3 z-[60]">
                        <div
                          className={`rounded-xl shadow-lg ${isScrolled
                            ? 'bg-white border border-[#E4E4F6] backdrop-blur'
                            : isHomePage
                              ? 'bg-white'
                              : 'bg-white'
                            } border border-[#E4E4F6]`}
                          style={{ width: '1079px' }}
                          onMouseEnter={() => setOpenDropdown('paint')}
                          onMouseLeave={() => setOpenDropdown(null)}
                        >
                          <div className="p-8">
                            <div className="grid grid-cols-3 gap-11 items-stretch">
                              {/* Category columns */}
                              {splitIntoColumns(paintCategories, 2).map(
                                (col, idx) => (
                                  <div
                                    key={idx}
                                    className="space-y-6 first:border-r-0 border-r border-[#535353]"
                                  >
                                    {col.map((cat) => {
                                      const slug = slugify(cat);
                                      return (
                                        <Link
                                          key={cat}
                                          to={getLocalizedPath(
                                            `/paint/${slug}`
                                          )}
                                          onClick={() => setOpenDropdown(null)}
                                          className="block text-[14px] leading-[19px] font-medium inter text-[#2F2F2F] hover:text-black transition-colors rounded-md px-3"
                                        >
                                          {t(`categories.${cat}`, cat)}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )
                              )}

                              {/* Featured item panel */}
                              <div className="w-full">
                                <div className="bg-white rounded-[15px] max-w-[287px] mx-auto w-full hover:shadow-sm transition-all duration-300">
                                  <a
                                    href={getLocalizedPath('/paint')}
                                    className="block"
                                    onClick={() => setOpenDropdown(null)}
                                  >
                                    <div className="rounded-[15px] bg-[#EDEDED] overflow-hidden h-[227px] flex items-center justify-center">
                                      <img
                                        src={
                                          (featuredPaint?.images &&
                                            featuredPaint.images[0]) ||
                                          '/assets/product-image.webp'
                                        }
                                        alt={featuredPaint?.name || 'product'}
                                        className="w-full h-full object-contain"
                                      />
                                    </div>
                                  </a>
                                  <div className="pt-6 text-center pb-4">
                                    <p className="text-[#A0A0A0] text-[12px] leading-[24px] font-medium">
                                      Featured
                                    </p>
                                    <h3 className="text-[#1D1D1F] text-[16px] leading-[24px] font-medium mt-[9px]">
                                      {featuredPaint?.name || '—'}
                                    </h3>
                                    <p className="text-[#1D1D1F] text-[14px] leading-[19px] font-medium">
                                      {featuredPaint?.price
                                        ? formatPrice(featuredPaint.price)
                                        : formatPrice(0)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : item.key === 'workwear' ? (
                  <div
                    key={item.key}
                    className="px-3 first:pl-0 last:pr-0 relative"
                    onMouseEnter={() => setOpenDropdown('workwear')}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <Link
                      to={item.href}
                      className={`group inline-flex items-center gap-1 text-[14px] leading-[16px] font-medium transition duration-200 border-b pb-[1.5px] ${isMenuOpen || isScrolled
                        ? 'text-white'
                        : isHomePage
                          ? 'text-white'
                          : 'text-black'
                        } ${isActive(item.href)
                          ? isMenuOpen || isScrolled
                            ? 'border-white'
                            : isHomePage
                              ? 'border-white'
                              : 'border-black'
                          : `border-transparent ${isMenuOpen || isScrolled
                            ? 'hover:border-white'
                            : isHomePage
                              ? 'hover:border-white'
                              : 'hover:border-black'
                          }`
                        }`}
                    >
                      {item.name}
                      <svg
                        className={`h-4 w-4 transition-transform duration-200 ${openDropdown === 'workwear' ? 'rotate-180' : 'rotate-0'}`}
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.08496 6.73828L8.08496 10.7383L12.085 6.73828"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>

                    {/* workwear */}
                    {openDropdown === 'workwear' && !isMenuOpen && (
                      <div className="absolute left-0 top-full pt-3 z-[60]">
                        <div
                          className={`rounded-xl shadow-lg ${isScrolled
                            ? 'bg-white backdrop-blur'
                            : isHomePage
                              ? 'bg-white'
                              : 'bg-white'
                            } border border-[#E4E4F6]`}
                          style={{ width: '715px' }}
                          onMouseEnter={() => setOpenDropdown('workwear')}
                          onMouseLeave={() => setOpenDropdown(null)}
                        >
                          <div className="p-8">
                            <div className="grid grid-cols-2 gap-6 items-stretch">
                              {/* Category columns */}
                              {splitIntoColumns(workwearCategories, 1).map(
                                (col, idx) => (
                                  <div
                                    key={idx}
                                    className="space-y-6 border-r border-[#535353]"
                                  >
                                    {col.map((cat) => {
                                      const slug = slugify(cat);
                                      return (
                                        <Link
                                          key={cat}
                                          to={getLocalizedPath(
                                            `/workwear/${slug}`
                                          )}
                                          onClick={() => setOpenDropdown(null)}
                                          className="block text-[14px] leading-[19px] font-medium inter text-[#2F2F2F] hover:text-black transition-colors px-3"
                                        >
                                          {t(`categories.${cat}`, cat)}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )
                              )}

                              {/* Featured item panel */}
                              <div className="bg-white rounded-[15px] max-w=[287px] mx-auto w-full hover:shadow-sm transition-all duration-300">
                                <a
                                  href={getLocalizedPath('/workwear')}
                                  className="block"
                                  onClick={() => setOpenDropdown(null)}
                                >
                                  <div className="rounded-[15px] bg-[#EDEDED] overflow-hidden h-[227px] flex items-center justify-center">
                                    <img
                                      src={
                                        (featuredWorkwear?.images &&
                                          featuredWorkwear.images[0]) ||
                                        '/assets/product-image.webp'
                                      }
                                      alt={featuredWorkwear?.name || 'product'}
                                      className="w-full h-full object-contain"
                                    />
                                  </div>
                                </a>
                                <div className="pt-6 text-center pb-4">
                                  <p className="text-[#A0A0A0] text-[12px] leading-[24px] font-medium">
                                    Featured
                                  </p>
                                  <h3 className="text-[#1D1D1F] text-[16px] leading-[24px] font-medium mt-[9px]">
                                    {featuredWorkwear?.name || '—'}
                                  </h3>
                                  <p className="text-[#1D1D1F] text-[14px] leading-[19px] font-medium">
                                    {featuredWorkwear?.price
                                      ? formatPrice(featuredWorkwear.price)
                                      : formatPrice(0)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-3 first:pl-0 last:pr-0" key={item.key}>
                    <Link
                      to={item.href}
                      className={`text-[14px] leading-[16px] font-medium transition duration-200 border-b pb-[1.5px] ${isMenuOpen || isScrolled
                        ? 'text-white'
                        : isHomePage
                          ? 'text-white'
                          : 'text-black'
                        } ${isActive(item.href)
                          ? isMenuOpen || isScrolled
                            ? 'border-white'
                            : isHomePage
                              ? 'border-white'
                              : 'border-black'
                          : `border-transparent ${isMenuOpen || isScrolled
                            ? 'hover:border-white'
                            : isHomePage
                              ? 'hover:border-white'
                              : 'hover:border-black'
                          }`
                        }`}
                    >
                      {item.name}
                    </Link>
                  </div>
                )
              )}
            </div>

            <Link
              to={getLocalizedPath('/')}
              className={`transition-all duration-300 ${isScrolled
                ? 'xl:absolute xl:left-1/2 xl:transform xl:-translate-x-1/2'
                : 'xl:absolute xl:left-1/2 xl:transform xl:-translate-x-1/2'
                }`}
            >
              <img
                src={
                  isMenuOpen || isScrolled
                    ? '/assets/logo-white.svg'
                    : isHomePage
                      ? '/assets/logo-white.svg'
                      : '/assets/logo-black.svg'
                }
                alt="GK Distribution"
                className="md:w-[148px] md:h-[78px] w-[120px] h-[63px]"
              />
            </Link>

            <div className="flex items-center gap-[15px]">
              {user?.role === 'admin' && (
                <a
                  href="/admin"
                  className="block text-[16px] leading-[19px] font-semibold inter text-nowrap text-[#2F2F2F] bg-gray-100 hover:bg-gray-200 transition-colors rounded-md px-4 py-1.5"
                >
                  Admin
                </a>
              )}

              <div className="hidden xl:flex items-center gap-3 pr-[12px]">
                <CurrencySwitcher />

                <LanguageSwitcher />

                {/* Search Button */}
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className={`p-2 hidden xl:block ${isMenuOpen || isScrolled ? 'text-white hover:text-gray-200' : isHomePage ? 'text-white hover:text-gray-200' : 'text-gray-700 hover:text-black'} transition duration-200`}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>

                {/* Profile Section */}
                <div className="relative profile-dropdown">
                  {!isLoggedIn ? (
                    <button
                      onClick={handleLogin}
                      className={`${isMenuOpen || isScrolled ? 'text-white hover:text-gray-200' : isHomePage ? 'text-white hover:text-gray-200' : 'text-gray-700 hover:text-black'} transition duration-200`}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 18C4 16.9391 4.42143 15.9217 5.17157 15.1716C5.92172 14.4214 6.93913 14 8 14H16C17.0609 14 18.0783 14.4214 18.8284 15.1716C19.5786 15.9217 20 16.9391 20 18C20 18.5304 19.7893 19.0391 19.4142 19.4142C19.0391 19.7893 18.5304 20 18 20H6C5.46957 20 4.96086 19.7893 4.58579 19.4142C4.21071 19.0391 4 18.5304 4 18Z"
                          stroke={
                            isMenuOpen || isScrolled
                              ? 'white'
                              : isHomePage
                                ? 'white'
                                : 'black'
                          }
                          strokeWidth="2"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 10C13.6569 10 15 8.65685 15 7C15 5.34315 13.6569 4 12 4C10.3431 4 9 5.34315 9 7C9 8.65685 10.3431 10 12 10Z"
                          stroke={
                            isMenuOpen || isScrolled
                              ? 'white'
                              : isHomePage
                                ? 'white'
                                : 'black'
                          }
                          strokeWidth="2"
                        />
                      </svg>
                    </button>
                  ) : (
                    <div className="relative">
                      <button
                        onClick={toggleProfileDropdown}
                        className={`${isMenuOpen || isScrolled ? 'text-white hover:text-gray-200' : isHomePage ? 'text-white hover:text-gray-200' : 'text-gray-700 hover:text-black'} transition duration-200`}
                        aria-haspopup="menu"
                        aria-expanded={showProfileDropdown}
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {user?.firstName?.[0]}
                            {user?.lastName?.[0]}
                          </span>
                        </div>
                      </button>
                      {showProfileDropdown && (
                        <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-[70]">
                          <div className="py-1 text-sm text-gray-700">
                            <Link
                              to={getLocalizedPath('/orders')}
                              className="block px-4 py-2 hover:bg-gray-50"
                            >
                              {t('nav.user.orders')}
                            </Link>
                            <Link
                              to={getLocalizedPath('/account')}
                              className="block px-4 py-2 hover:bg-gray-50"
                            >
                              {t('nav.user.account')}
                            </Link>
                            <Link
                              to={getLocalizedPath('/contact')}
                              className="block px-4 py-2 hover:bg-gray-50"
                            >
                              {t('nav.user.contact')}
                            </Link>
                            {user?.role === 'admin' && (
                              <a
                                href="/admin"
                                className="block px-4 py-2 hover:bg-gray-50"
                              >
                                {t('nav.user.admin')}
                              </a>
                            )}
                            <button
                              onClick={handleLogout}
                              className="w-full text-left px-4 py-2 hover:bg-gray-50"
                            >
                              {t('nav.user.signout')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Cart button */}
              <Link
                to={getLocalizedPath('/cart')}
                aria-label="Cart"
                className={`hidden xl:flex items-center justify-center w-[36px] h-[36px] rounded-md ${isMenuOpen || isScrolled ? 'bg-white/10 text-white' : isHomePage ? 'bg-white/10 text-white' : 'bg-black text-white'} hover:opacity-90`}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 6H21L19 14H8L6 6Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="9" cy="19" r="1.5" fill="currentColor" />
                  <circle cx="18" cy="19" r="1.5" fill="currentColor" />
                </svg>
              </Link>

              <button className="hidden xl:block bg-[#000000] rounded-[9px] h-[32px] px-5 text-[14px] leading-[16px] font-medium text-white">
                {t('nav.shopAll')}
              </button>

              <button
                onClick={handleMenuToggle}
                className={`xl:hidden p-2 ${isMenuOpen || isScrolled
                  ? 'text-white hover:text-gray-200'
                  : isHomePage
                    ? 'text-white hover:text-gray-200'
                    : 'text-gray-700 hover:text-black'
                  } focus:outline-none`}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke={
                    isScrolled ? 'white' : isHomePage ? 'white' : 'currentColor'
                  }
                >
                  {isMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Sidebar */}
          {(isMenuOpen || isClosing) && (
            <>
              <div
                className={`xl:hidden fixed top-0 left-0 right-0 h-[calc(100vh)] w-full bg-white z-50 pb-6 transform transition-transform duration-300 ease-in-out ${isClosing ? 'translate-x-full' : 'translate-x-0'
                  }`}
                style={{
                  animation: isClosing
                    ? 'slideOutToRight 0.3s ease-in-out'
                    : 'slideInFromRight 0.3s ease-in-out',
                }}
              >
                <div className="flex flex-col h-full">
                  {/* Mobile menu top bar */}
                  <div className="sticky top-0 z-10 bg-white h-[78px] flex items-center justify-between px-4 border-b border-gray-200">
                    <Link to={getLocalizedPath('/')}>
                      <img
                        src="/assets/logo-black.svg"
                        alt="GK Distribution"
                        className="w-[120px] h-[63px] object-contain"
                      />
                    </Link>
                    <button
                      onClick={handleMenuClose}
                      className="p-2 text-gray-700 hover:text-black focus:outline-none"
                      aria-label="Close menu"
                    >
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
                    {navigation.map((item) => (
                      <Link
                        key={item.key}
                        to={item.href}
                        className={`block px-4 py-2.5 text-base font-medium rounded-lg transition duration-200 ${isActive(item.href)
                          ? 'bg-black text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        onClick={handleMenuClose}
                      >
                        {item.name}
                      </Link>
                    ))}
                    {/* Mobile View Cart */}
                    <Link
                      to={getLocalizedPath('/cart')}
                      className="block py-2.5 text-base font-medium rounded-lg bg-black text-white text-center"
                      onClick={handleMenuClose}
                    >
                      View Cart
                    </Link>
                  </div>

                  <div className="w-full p-4 pb-10 border-t border-gray-200 space-y-4">
                    <div className="w-full flex items-center justify-center gap-8">
                      <CurrencySwitcher />
                      <LanguageSwitcher />
                    </div>
                    {/* Mobile Profile Section */}
                    <div className='flex items-center flex-col justify-between w-full gap-2'>
                      <div className="w-full flex items-center justify-center">
                        {!isLoggedIn ? (
                          <button
                            onClick={handleLogin}
                            className="w-full sm:w-fit bg-black text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition duration-200"
                          >
                            Login
                          </button>
                        ) : (
                          <div className="w-full space-y-2">
                            <div className="text-center py-2">
                              <p className="text-sm font-medium text-gray-900">
                                John Doe
                              </p>
                              <p className="text-xs text-gray-500">
                                john.doe@example.com
                              </p>
                            </div>
                            <div className="flex gap-2">
                              {user?.role === 'admin' && (
                                <Link
                                  to="/admin/settings"
                                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition duration-200 text-center"
                                  onClick={handleMenuClose}
                                >
                                  Settings
                                </Link>
                              )}
                              <button
                                onClick={handleLogout}
                                className="flex-1 bg-red-100 text-red-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-200 transition duration-200"
                              >
                                Logout
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="w-full flex items-center justify-center">
                        <button className="w-full sm:w-fit bg-black text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition duration-200">
                          Shop All
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </nav>
    </>
  );
};

export default Header;
